import { css, cx } from 'emotion';
import React, { ChangeEvent, ReactNode, useEffect, useMemo, useRef } from 'react';

import { BorderRadius, Color, FontSize, LineHeight } from '../../constants/Style';
import { square } from '../../helpers/Style';

type Props = Partial<{
  label: string;
  checked: boolean;
  value: string;
  disabled: boolean;
  indeterminate: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  suffix?: ReactNode;
  analyticsClassName: string;
}>;

export const Checkbox = ({
  label = '',
  checked,
  value,
  disabled,
  indeterminate = false,
  onChange,
  suffix,
  analyticsClassName,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const symbolStyle = useMemo(
    () => (indeterminate ? symbolIndeterminateStyle : checked ? symbolCheckedStyle : ''),
    [indeterminate, checked],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => !disabled && onChange?.(e);

  useEffect(() => {
    const instance = inputRef.current;
    if (!instance) return;
    instance.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label className={`${baseStyle} ${analyticsClassName}`}>
      <input
        className={inputStyle}
        type="checkbox"
        ref={inputRef}
        checked={checked}
        value={value}
        disabled={disabled}
        onChange={handleChange}
      />
      <span className={cx(indicatorStyle, (checked || indeterminate) && indicatorActiveStyle)}>
        <span className={symbolStyle} />
      </span>
      <span className={labelStyle}>
        {label}
        {suffix ? <span className={suffixStyle}>{suffix}</span> : null}
      </span>
    </label>
  );
};

const baseStyle = css`
  position: relative;
  display: inline-grid;
  grid-template-columns: 20px auto;
  align-items: center;
`;

const inputStyle = css`
  /* 視覚上見えないが操作可能な Style 指定 */

  /* 参考: https://github.com/twbs/bootstrap/blob/a4a04cd9ec741050390746f8056cc79a9c04c8df/scss/mixins/_screen-reader.scss#L8-L18 */
  position: absolute;
  padding: 0;
  margin: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
  ${square(1)}
`;

const indicatorStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${Color.GrayscaleWhite};
  border: 2px solid ${Color.GrayscaleLight};
  border-radius: ${BorderRadius.Small};
  ${square(20)}
`;

const indicatorActiveStyle = css`
  background: ${Color.GrayscaleDarker};
  border-color: ${Color.GrayscaleDarker};
`;

const symbolIndeterminateStyle = css`
  width: 10px;
  height: 2px;
  background: ${Color.GrayscaleWhite};
`;

const symbolCheckedStyle = css`
  display: inline-block;
  width: 5px;
  height: 8px;
  margin-top: -2px;
  border-right: 2px solid ${Color.GrayscaleWhite};
  border-bottom: 2px solid ${Color.GrayscaleWhite};
  transform: rotate(40deg);
`;

const labelStyle = css`
  display: flex;
  align-items: center;
  margin-left: 10px;
  font-size: ${FontSize.Medium};
  line-height: ${LineHeight.Compressed};
  white-space: pre-wrap;
  user-select: none;

  &:empty {
    margin-left: 0;
  }
`;

const suffixStyle = css`
  flex-grow: 1;
  margin-left: 10px;

  > * {
    display: block;
  }
`;
